// © 2026 김용현
/**
 * 실험 목록 — 실험실 창에 보이는 카드.
 *
 * 구현된 실험만 적는다. 단계가 끝날 때 한 줄씩 늘고, 정식 승격 때 한 줄씩 준다.
 * id 는 URL(?lab=a,b)·저장 키·labs.isOn(id) 에 그대로 쓰인다.
 * 설계: docs/superpowers/specs/2026-09-25-labs-design.md
 *
 * details 는 카드의 i 버튼을 누르면 펼쳐지는 안내다(글자 그대로, HTML 아님).
 *   intro: 무엇이고 왜 쓰는지 (문단 배열)
 *   steps: 사용 방법 (순서대로)
 *   notes: 알아 두기 — 한계·주의 (없으면 빈 배열)
 * 내용은 docs/사용설명서.md 「1-14. 실험실」과 맞춘다.
 */

export const EXPERIMENTS = [
  {
    id: 'glass',
    name: '글래스 UI',
    summary: '패널·메뉴·범례를 반투명 유리로 보여줍니다.',
    since: '2026-09',
    details: {
      intro: [
        '지도가 창 전체에 깔리고, 메뉴바·툴바·왼쪽 패널·상태줄이 가장자리에서 조금 띄운 둥근 유리 카드로 그 위에 뜹니다. 유리 너머로 지도가 흐릿하게 비쳐 화면이 넓어 보이고, 지도 가장자리까지 한눈에 들어옵니다.',
        '범례·피처 정보 카드·축척바·드롭다운 메뉴도 같은 유리로 바뀝니다. 밝은 테마와 어두운 테마 모두에서 쓸 수 있습니다.'
      ],
      steps: [
        '스위치를 켜면 바로 유리 화면으로 바뀝니다. 새로고침은 필요 없습니다.',
        '버튼에 마우스를 올리면 살짝 밝아지고, 누르면 유리를 누른 듯 안으로 들어갑니다.',
        '범례·축척바는 끌어서 옮길 수 있습니다. 왼쪽 패널·툴바·상태줄 밑으로는 들어가지 않습니다.',
        '오른쪽 위 해/달 버튼으로 테마를 바꾸면 어두운 유리가 됩니다. 어두운 유리에서는 글자가 흰색입니다.',
        '태블릿·휴대폰에서도 같은 유리가 적용됩니다. 휴대폰은 흐림을 조금 줄여 그립니다.'
      ],
      notes: [
        '글래스가 켜진 채 지도 이미지를 내보내면 패널·막대에 가려진 지도 영역까지 찍힙니다. 내보내기 전에 잠시 끄는 것을 권합니다.',
        '운영체제의 "투명 효과 끄기" 설정은 무시합니다. 이 실험을 직접 켠 경우에만 적용되기 때문입니다.',
        '흐림 효과는 그래픽 처리 부담이 있어 오래된 태블릿에서는 화면이 조금 느려질 수 있습니다.'
      ]
    }
  },
  {
    id: 'class-fill',
    name: '구간 채움 편집',
    summary: '범례의 색 칸을 눌러 구간마다 색·패턴·이미지·질감을 바꿉니다.',
    since: '2026-09',
    details: {
      intro: [
        '단계구분도(격자 포함)의 구간마다 채움을 따로 정합니다. 단색뿐 아니라 사선·점·격자 패턴, 내 이미지, 종이·광택·모래·숲·물 질감을 쓸 수 있습니다.',
        '흑백으로 인쇄하는 학습지처럼 색만으로 구간을 구별하기 어려울 때, 또는 주제에 어울리는 질감으로 지도를 꾸미고 싶을 때 씁니다.'
      ],
      steps: [
        '주제도 메뉴에서 단계구분도를 만들어 범례가 보이게 합니다.',
        '범례의 색 칸을 누르면 그 구간의 채움을 고르는 창이 뜹니다.',
        '단색·패턴·이미지·질감 가운데 하나를 고릅니다. 패턴은 색·간격·굵기·각도와 배경을, 질감은 종류와 강도를 정합니다. 이미지는 PNG·JPG·SVG 파일을 올리면 타일처럼 반복됩니다.',
        '바꾸는 즉시 지도와 범례에 반영됩니다. 다른 색 칸을 눌러 구간마다 반복합니다.',
        '창 아래 「모든 구간에 프리셋」으로 흑백 인쇄용 사선·점 밀도 단계·종이 질감 통일을 한 번에 걸 수 있습니다. 「팔레트로 되돌리기」는 모든 구간을 단색으로 되돌립니다.'
      ],
      notes: [
        '바꾼 채움은 프로젝트 저장·복원과 지도 내보내기(PNG·PDF 범례)에 그대로 실립니다.',
        '패턴과 질감은 화면에 고정된 무늬라, 지도를 끄는 동안 함께 움직였다가 손을 놓으면 다시 정렬됩니다.',
        '이미지 채움은 긴 변 256픽셀로 줄여 저장하며, 구간당 약 200KB까지 프로젝트 파일이 커질 수 있습니다.',
        '카토그램·히트맵 범례에는 아직 적용되지 않습니다.'
      ]
    }
  },
  {
    id: 'globe',
    name: '지구본·투영법 보기',
    summary: '지금 지도를 지구본으로 돌려 보고 투영법을 바꿔 봅니다.',
    since: '2026-09',
    details: {
      intro: [
        '지금 지도의 벡터 레이어(단계구분도·격자·카토그램 포함)를 둥근 지구본 위에 그립니다. 같은 자료를 메르카토르·등면적(Equal Earth)·내추럴 어스·방위 등면적·등장방형 투영으로 바꿔 가며 볼 수 있습니다.',
        '웹 지도가 쓰는 메르카토르에서 고위도가 얼마나 부풀려지는지, 등면적 투영에서는 어떻게 달라지는지 학생이 직접 돌려 보며 비교하는 수업에 알맞습니다.'
      ],
      steps: [
        '스위치를 켜면 툴바 실험실 버튼 옆에 「지구본」 버튼이 나타납니다.',
        '지구본 버튼을 누르면 2D 지도의 가운데가 정면에 온 지구본이 열립니다.',
        '끌어서 돌리고, 휠이나 두 손가락으로 확대합니다. 더블클릭하면 처음 자세로 돌아갑니다.',
        '오른쪽 위 상자의 「투영법」에서 다른 투영을 고릅니다. 원통 투영에서는 좌우(경도 방향)로만 돕니다.',
        '「경위선」·「배경 육지」 체크박스로 배경을 끄고 켭니다. 「PNG로 저장」은 지구본 화면만 저장합니다.',
        '「닫기」나 지구본 버튼을 다시 누르면 2D 지도로 돌아갑니다. 정면에 있던 곳이 2D 지도의 가운데가 됩니다.'
      ],
      notes: [
        '래스터·DEM·히트맵·도형표현도·흐름도와 레이어 라벨은 지구본에 그려지지 않습니다. 해당 레이어는 상자에 이름과 함께 안내됩니다.',
        '배경은 배경지도 타일이 아니라 바다색 구와 경위선, 내장 「세계 국가」 육지입니다.',
        '3D 보기·스와이프 비교와는 동시에 켤 수 없습니다. 한쪽을 켜면 다른 쪽이 꺼집니다.'
      ]
    }
  },
  {
    id: 'swipe',
    name: '스와이프 비교',
    summary: '레이어나 배경지도 둘을 가운데 막대를 끌어 비교합니다.',
    since: '2026-09',
    details: {
      intro: [
        '지도 가운데에 막대를 두고, 막대 한쪽에만 고른 레이어나 배경지도를 그립니다. 막대를 끌어 옮기면 같은 자리의 두 모습을 번갈아 볼 수 있습니다.',
        '위성 사진과 일반 지도, 두 해의 단계구분도, 토지 이용과 지형처럼 같은 지역의 두 자료를 겹쳐 비교할 때 씁니다.'
      ],
      steps: [
        '스위치를 켜면 툴바 실험실 묶음에 「스와이프」 버튼이 나타납니다.',
        '스와이프 버튼을 누르면 오른쪽 위에 비교 상자가 열립니다.',
        '「비교 대상」에서 레이어 하나 또는 지금과 다른 배경지도를 고릅니다.',
        '「방향」에서 세로 막대(좌우 비교)나 가로 막대(위아래 비교)를 고릅니다. 대상은 막대의 왼쪽(또는 위)에만 그려집니다.',
        '지도 가운데 막대를 끌어 옮기며 비교합니다.',
        '끝나면 「닫기」를 누르거나 스와이프 버튼을 다시 누릅니다.'
      ],
      notes: [
        '비교 상태는 프로젝트에 저장되지 않습니다. 그때그때 쓰는 도구입니다.',
        '배경지도를 비교할 때 지명 같은 라벨 오버레이는 함께 얹지 않습니다.',
        '히트맵·흐름도 레이어는 잘라 그릴 수 없어 비교 대상 목록에 나오지 않습니다.',
        '지도 이미지 내보내기에는 막대 없이 잘린 모습 그대로 찍힙니다. 3D 보기·지구본과는 함께 쓸 수 없습니다.'
      ]
    }
  },
  {
    id: 'time-series',
    name: '시계열 단계구분도',
    summary: '연도별 열을 슬라이더로 넘기며 변화를 보고, GIF·동영상으로 저장합니다.',
    since: '2026-09',
    details: {
      intro: [
        '면 레이어의 연도별 숫자 열(예: 2015·2020·2025년 인구) 여러 개로 단계구분도를 만들고, 슬라이더로 연도를 넘기며 변화를 봅니다.',
        '모든 연도의 값을 합쳐 구간을 한 번만 계산해 고정하므로, 같은 색은 어느 연도에서나 같은 값의 범위를 뜻합니다. 그래서 색의 변화로 연도 사이를 바로 비교할 수 있습니다. 만든 애니메이션은 GIF나 동영상으로 저장해 발표 자료에 넣을 수 있습니다.'
      ],
      steps: [
        '스위치를 켜면 주제도 메뉴의 단계구분도 아래에 「시계열 단계구분도」가 나타납니다.',
        '설정 창에서 레이어(숫자 열이 2개 이상인 면 레이어)를 고르고, 필드 목록에서 연도 열을 2개 이상 체크합니다. 「연도 자동 선택」은 이름이 연도로 시작하는 열을 한 번에 골라 줍니다.',
        '분류 방법·분류 수·색상 팔레트를 정하고 「적용」을 누릅니다. 원본은 그대로 두고 「원본이름_시계열_첫필드~끝필드」 레이어가 새로 생깁니다.',
        '지도 아래 슬라이더로 연도를 넘기거나 「재생」으로 자동 반복합니다. 속도는 0.5×·1×·2× 중에서 고릅니다.',
        '「저장」을 누르고 형식(GIF·동영상)·배율·프레임 유지 시간·연도 라벨·범례 포함 여부를 정한 뒤 「만들기」를 누릅니다.',
        '「닫기」는 슬라이더만 걷고 레이어는 남겨 둡니다.'
      ],
      notes: [
        '동영상은 브라우저가 지원하면 MP4, 아니면 WebM 으로 만들어집니다. 만들기 버튼에 적힌 확장자를 확인하세요. PowerPoint 에는 WebM 을 넣을 수 없습니다.',
        '동영상은 실시간으로 녹화되므로 연도 10개 × 1.2초면 약 12초가 걸립니다.',
        '시계열 레이어는 프로젝트에 함께 저장되며, 다시 열면 보고 있던 연도의 단계구분도로 섭니다.',
        '데스크톱 앱(e-GIStory)에서는 애니메이션 저장이 되지 않을 수 있으니 웹 브라우저에서 하세요.'
      ]
    }
  }
];

export const EXPERIMENT_IDS = EXPERIMENTS.map((e) => e.id);

/** 의견 보내기(구글 폼) 주소 — 실험 전체에 하나, 어느 기능인지는 폼에서 고른다. 비어 있으면 실험실 창에 링크가 안 보인다. */
export const FEEDBACK_URL = 'https://forms.gle/qwo8HJPCupahHU4T8';

/**
 * 의견 저장 웹앱(Apps Script, scripts/labs-feedback.gs 를 배포한 /exec 주소).
 * 있으면 「의견 보내기」가 구글 폼 대신 e-GIS 안의 의견 창(LabFeedbackPanel)을 열고, 답은 스프레드시트에 쌓인다.
 */
export const FEEDBACK_ENDPOINT = 'https://script.google.com/macros/s/AKfycby67a3zL4G1PzDdmgJCjRLNXXOlK-iByjzDDCYnHMMxjQtR22zAaVUvaD-cu2-GuKpdtQ/exec';
